"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Moon, Sun } from "lucide-react";
import { supabase } from "@/lib/supabase"; // Adjust path based on your project structure

// ===================== Interfaces =====================
interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  color: string;
  opacity: number;
  life: number;
}

interface SupabaseUser {
  id: string;
  email?: string;
}

// ===================== Particle Background =====================
const ParticleBackground = ({ active = true }: { active?: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const activeRef = useRef(active);
  const particlesRef = useRef<Particle[]>([]);
  const animateRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    activeRef.current = active;

    const shouldRun = active && !document.hidden;
    if (!shouldRun) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    if (rafRef.current == null && animateRef.current) {
      rafRef.current = requestAnimationFrame(animateRef.current);
    }
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const setup = () => {
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const { offsetWidth, offsetHeight } = canvas;
      canvas.width = offsetWidth * dpr;
      canvas.height = offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, offsetWidth, offsetHeight);
    };

    const ensureParticles = () => {
      if (particlesRef.current.length > 0) return;
      const count = 20; // Reduced for performance
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      for (let i = 0; i < count; i++) {
        particlesRef.current.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: Math.random() * 2.6 + 0.6,
          speedX: (Math.random() - 0.5) * 0.45,
          speedY: (Math.random() - 0.5) * 0.45,
          color: `hsl(${Math.random() * 90 + 180}, 80%, 60%)`,
          opacity: Math.random() * 0.4 + 0.2,
          life: Math.random() * 100,
        });
      }
    };

    const stop = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const animate = () => {
      const isActive = activeRef.current;
      const isPaused = pausedRef.current || document.hidden;

      if (!isActive || isPaused || !canvasRef.current) {
        stop();
        return;
      }

      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.speedX;
        p.y += p.speedY;
        p.life += 0.08;
        p.opacity = Math.max(0.1, Math.sin(p.life) * 0.3 + 0.3);

        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(")", `, ${p.opacity})`).replace("hsl", "hsla");
        ctx.shadowColor = p.color.replace(")", `, ${p.opacity * 0.8})`).replace("hsl", "hsla");
        ctx.shadowBlur = 12;
        ctx.fill();
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 95) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.12 * (1 - dist / 95)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    animateRef.current = animate;

    const handleVisibility = () => {
      pausedRef.current = document.hidden;
      if (!pausedRef.current && activeRef.current && rafRef.current == null && animateRef.current) {
        rafRef.current = requestAnimationFrame(animateRef.current);
      }
      if (pausedRef.current) stop();
    };

    setup();
    ensureParticles();

    window.addEventListener("resize", setup);
    document.addEventListener("visibilitychange", handleVisibility);

    if (!document.hidden && activeRef.current && animateRef.current) {
      rafRef.current = requestAnimationFrame(animateRef.current);
    }

    return () => {
      stop();
      window.removeEventListener("resize", setup);
      document.removeEventListener("visibilitychange", handleVisibility);
      animateRef.current = null;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-40 pointer-events-none"
      aria-hidden="true"
    />
  );
};

// ===================== Animated Icons =====================
const AnimatedIcons = {
  User: ({ active, className }: { active: boolean; className: string }) => {
    const uid = React.useId();
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id={`userGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
          <filter id={`glowUser-${uid}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d="M20 21V19C20 17.94 19.58 16.92 18.83 16.17C18.08 15.42 17.06 15 16 15H8C6.94 15 5.92 15.42 5.17 16.17C4.42 16.92 4 17.94 4 19V21"
          stroke={active ? `url(#userGrad-${uid})` : "currentColor"}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={active ? "animate-pulse-slow" : ""}
        />
        <path
          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z"
          stroke={active ? `url(#userGrad-${uid})` : "currentColor"}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={active ? `url(#glowUser-${uid})` : "none"}
          className={`transition-all duration-500 ease-out ${
            active ? "fill-purple-500/15 drop-shadow-xl animate-bounce" : "fill-none hover:fill-gray-100/50"
          }`}
        />
        {active && (
          <>
            <circle cx="12" cy="8" r="3" fill={`url(#userGrad-${uid})`} className="animate-bounce opacity-60" />
            <circle cx="12" cy="8" r="1.5" fill={`url(#userGrad-${uid})`} className="animate-ping opacity-30" />
          </>
        )}
      </svg>
    );
  },
};

function ThemeToggle() {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (stored) {
      // Usar tema almacenado
      setTheme(stored);
      document.documentElement.classList.toggle("dark", stored === "dark");
    } else {
      // Tema automático por hora
      const hour = new Date().getHours();
      const isNight = hour < 6 || hour > 20; // Oscuro entre 8 PM y 6 AM
      const autoTheme = isNight ? "dark" : "light";
      setTheme(autoTheme);
      document.documentElement.classList.toggle("dark", autoTheme === "dark");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
  };

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-full bg-white/70 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 shadow-sm hover:scale-110 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-300"
      aria-label={`Cambiar a tema ${theme === "dark" ? "claro" : "oscuro"}`}
    >
      {theme === "dark" ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-slate-700" />}
    </button>
  );
}

// ===================== Flotantes =====================
const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="absolute w-3 h-3 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-30"
        style={{
          top: `${15 + i * 20}%`,
          left: `${10 + i * 25}%`,
          animation: `float${i + 1} ${6 + i}s ease-in-out infinite`,
          filter: "drop-shadow(0 0 6px rgba(99,102,241,0.35))",
        }}
      />
    ))}
    <style jsx>{`
      @keyframes float1 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(25px, -15px) rotate(180deg) scale(1.2); }
      }
      @keyframes float2 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(-20px, 20px) rotate(-180deg) scale(1.25); }
      }
      @keyframes float3 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(15px, -25px) rotate(90deg) scale(1.1); }
      }
      @keyframes float4 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(-25px, 10px) rotate(-90deg) scale(1.18); }
      }
      @keyframes float5 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(20px, -20px) rotate(270deg) scale(1.28); }
      }
    `}</style>
  </div>
);

// ===================== Header principal =====================
export default function Header() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [businessTypeName, setBusinessTypeName] = useState<string>("Cargando...");
  const [companyName, setCompanyName] = useState<string>("Mi negocio");
  const [fullName, setFullName] = useState<string>("");
  const [roleName, setRoleName] = useState<string>("");
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [canHover, setCanHover] = useState(true);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  // ⏰ Reloj
  useEffect(() => {
    setMounted(true);
    setIsVisible(true);
  }, []);

  useEffect(() => {
    try {
      setCanHover(window.matchMedia("(hover: hover)").matches);
    } catch {
      setCanHover(false);
    }
  }, []);

  // 🧩 Cargar usuario y su giro
  useEffect(() => {
    const loadUserAndGiro = async () => {
      try {
        // Check and refresh session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error("Error fetching session:", sessionError);
          setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
          return;
        }

        const user = sessionData?.session?.user;
        if (!user) {
          setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
          return;
        }

        // Refresh session if about to expire
        if (sessionData.session?.expires_at && Date.now() > sessionData.session.expires_at * 1000 - 60000) {
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error("Error refreshing session:", refreshError);
            setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
            return;
          }
        }

        setSupabaseUser(user);

        // 🔹 Obtener perfil del usuario
        const { data: profile, error: profileErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileErr) {
          console.error("Error fetching profile:", profileErr);
          setBusinessTypeName("Error al cargar giro");
          return;
        }
        // Full name (from profiles.full_name)
        const rawFullName = (profile as any)?.full_name;
        if (typeof rawFullName === "string" && rawFullName.trim().length > 0) {
          setFullName(rawFullName.trim());
        } else {
          setFullName("Usuario");
        }

        // Role name (from roles.name)
        const roleId = (profile as any)?.role_id as string | undefined;
        if (roleId) {
          const { data: role, error: roleErr } = await supabase
            .from("roles")
            .select("name")
            .eq("id", roleId)
            .maybeSingle();

          if (roleErr) {
            console.error("Error fetching role:", roleErr);
            setRoleName("Sin rol");
          } else {
            const rawRoleName = (role as any)?.name;
            setRoleName(typeof rawRoleName === "string" && rawRoleName.trim().length > 0 ? rawRoleName.trim() : "Sin rol");
          }
        } else {
          setRoleName("Sin rol");
        }

        // Company name (from tenants.company_name)
        const tenantId = (profile as any)?.tenant_id as string | undefined;
        if (tenantId) {
          const { data: tenant, error: tenantErr } = await supabase
            .from("tenants")
            .select("company_name")
            .eq("id", tenantId)
            .maybeSingle();

          if (tenantErr) {
            console.error("Error fetching tenant:", tenantErr);
            setCompanyName("Mi negocio");
          } else {
            const rawCompanyName = (tenant as any)?.company_name;
            if (typeof rawCompanyName === "string" && rawCompanyName.trim().length > 0) {
              setCompanyName(rawCompanyName.trim());
            } else {
              setCompanyName("Mi negocio");
            }
          }
        } else {
          setCompanyName("Mi negocio");
        }

        if (!profile?.business_type_id) {
          setBusinessTypeName("Sin giro");
          return;
        }

        // 🔹 Obtener nombre del giro
        const { data: businessType, error: businessErr } = await supabase
          .from("business_types")
          .select("name")
          .eq("id", profile.business_type_id)
          .maybeSingle();

        if (businessErr) {
          console.error("Error fetching business type:", businessErr);
          setBusinessTypeName("Error al cargar giro");
          return;
        }

        setBusinessTypeName(businessType?.name ?? "Sin giro asignado");
      } catch (err) {
        console.error("Unexpected error:", err);
        setBusinessTypeName("Error al cargar");
      }
    };

    loadUserAndGiro();

    // Escuchar cambios de sesión
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
      } else {
        loadUserAndGiro();
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // 🔒 Cerrar sesión
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      
      router.replace("/auth/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  // 🔲 Clics fuera del dropdown
  useEffect(() => {
    if (!isProfileOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isProfileOpen]);

  if (!mounted) return null;

  return (
    <header
      onPointerEnter={() => setIsHeaderHovered(true)}
      onPointerLeave={() => setIsHeaderHovered(false)}
      className="relative border-b border-gray-200/20 bg-gradient-to-r from-white/95 via-blue-50/40 to-purple-50/40 dark:from-slate-900 dark:via-slate-800 dark:to-slate-800 backdrop-blur-xl shadow-2xl shadow-blue-500/15 sticky top-0 z-50 overflow-visible"
      role="banner"
    >
      <ParticleBackground active={canHover ? isHeaderHovered : true} />
      <FloatingElements />

      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500/60 to-transparent animate-[borderPulse_4s_ease-in-out_infinite] z-0" />

      <div className="relative min-h-[3.5rem] py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-4 z-10">
        {/* LEFT: Status / Context */}
        <div
          className="group flex flex-col min-w-0 flex-1 space-y-0.5 transition-all duration-1000 ease-out"
          style={{ opacity: isVisible ? 1 : 0, transform: `translateX(${isVisible ? 0 : -16}px)` }}
        >
          
          <div className="flex items-center space-x-2">
            <span className="text-xs sm:text-sm font-bold truncate max-w-[60vw] sm:max-w-[360px] bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient-x">
              {companyName}
            </span>

            <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse-slow" />
          </div>
          <div className="flex items-center space-x-1.5">
            <div className={`w-2 h-2 ${supabaseUser ? "bg-green-500" : "bg-gray-400"} rounded-full animate-ping opacity-60`} />
            <span className="text-[10px] text-gray-600 dark:text-gray-400 font-medium animate-pulse">
              {supabaseUser ? "Sistema activo" : "Sin conexión"}
            </span>
          </div>
        </div>

{/* DER: Acciones */}
        <div
          className="flex items-center gap-2 sm:gap-3 flex-shrink-0 transition-all duration-1000 ease-out delay-150 z-20"
          style={{ opacity: isVisible ? 1 : 0, transform: `translateX(${isVisible ? 0 : 16}px)` }}
        >
          {/* Giro */}
          <div className="relative group hidden md:block">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg blur opacity-20 group-hover:opacity-50 transition duration-700 group-hover:duration-200 animate-tilt" />
            <span
              className="relative bg-white/90 dark:bg-slate-800 backdrop-blur-md border border-blue-200/30 dark:border-slate-700 py-1 px-3 text-xs rounded-md font-semibold text-blue-700 dark:text-blue-400 shadow-md shadow-blue-500/15 hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:bg-white dark:hover:bg-slate-700 group-hover:text-blue-800 dark:group-hover:text-blue-300 opacity-0 animate-[fadeIn_0.7s_ease-out_0.25s_forwards]"
            >
              🎯 Giro: {businessTypeName}
            </span>
            <span className="pointer-events-none absolute -bottom-1 left-2 right-2 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
          </div>

          {/* BOTÓN DE TEMA */}
          <ThemeToggle />

          {/* PERFIL / SESIÓN */}
          {!supabaseUser ? (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative group">
                <button
                  onClick={() => router.push("/auth/login")}
                  className="relative flex items-center space-x-1.5 bg-white/90 dark:bg-slate-800 backdrop-blur-md border border-gray-200/30 dark:border-slate-700 py-1 px-2.5 text-xs rounded-md font-semibold text-gray-700 dark:text-gray-300 shadow-md shadow-gray-500/10 hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:bg-white dark:hover:bg-slate-700 hover:border-blue-300/40 dark:hover:border-blue-500/40 group opacity-0 animate-[fadeIn_0.7s_ease-out_0.35s_forwards] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  aria-label="Iniciar sesión"
                >
                  <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
                    <span className="absolute inset-0 scale-0 group-active:scale-100 bg-blue-500/10 rounded-md transition-transform duration-300 ease-out" />
                  </span>
                  <span>Iniciar sesión</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="relative group z-[90]" ref={dropdownRef}>
              <button
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
                onClick={() => setIsProfileOpen((v) => !v)}
                onMouseEnter={() => setHoveredItem("profile")}
                onMouseLeave={() => setHoveredItem(null)}
                className="relative flex items-center space-x-2 bg-white/90 dark:bg-slate-800 backdrop-blur-md border border-gray-200/30 dark:border-slate-700 py-1 px-2.5 rounded-md font-semibold text-gray-700 dark:text-gray-300 shadow-md shadow-gray-500/10 hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105 hover:bg-white dark:hover:bg-slate-700 hover:border-purple-300/40 dark:hover:border-purple-500/40 group opacity-0 animate-[fadeIn_0.7s_ease-out_0.45s_forwards] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50"
                aria-label="Menú de perfil"
              >
                <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
                  <span className="absolute inset-0 scale-0 group-active:scale-100 bg-purple-500/10 rounded-md transition-transform duration-300 ease-out" />
                </span>
                <div className="relative">
                  <div
                    className={`w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md shadow-purple-500/25 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${
                      isProfileOpen || hoveredItem === "profile" ? "animate-pulse-slow glow" : ""
                    }`}
                  >
                    <AnimatedIcons.User
                      active={isProfileOpen || hoveredItem === "profile"}
                      className="w-3 h-3 text-white"
                    />
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur opacity-20 group-hover:opacity-50 transition duration-700 group-hover:duration-200 animate-tilt" />
                </div>
                <div className="text-left hidden md:block min-w-0 max-w-[260px]">
                  <p className="text-xs font-bold bg-gradient-to-r from-gray-700 to-gray-900 dark:from-gray-300 dark:to-gray-100 bg-clip-text text-transparent">
                    {fullName || "Usuario"}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Conectado</p>
                </div>
                <svg
                  className={`w-3 h-3 transition-all duration-300 ${
                    isProfileOpen ? "rotate-180 text-purple-600 dark:text-purple-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isProfileOpen && (
                <div
                  role="menu"
                  aria-label="Menú de perfil"
                  className="absolute right-2 sm:right-0 top-12 mt-1 w-60 max-w-[calc(100vw-1rem)] bg-white/95 dark:bg-slate-800 backdrop-blur-xl rounded-lg shadow-2xl shadow-purple-500/25 border border-purple-200/30 dark:border-purple-500/30 origin-top-right animate-in fade-in slide-in-from-top-2 z-[100]"
                  style={{ animationDuration: "200ms" }}
                >
                  <div className="px-3 py-2 border-b border-gray-100/30 bg-gradient-to-r from-blue-50/50 to-purple-50/50 dark:from-slate-800/50 dark:to-slate-700/50 rounded-t-lg">
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{fullName || "Usuario"}</p>
                    <p className="text-[10px] text-gray-700 dark:text-gray-300">{supabaseUser.email}</p>
                    <p className="text-[10px] text-gray-600 dark:text-gray-400">Rol: {roleName || "Sin rol"}</p>
                    <p className="text-[10px] text-gray-600 dark:text-gray-400">Conectado</p>
                  </div>
                  <div className="p-1.5 space-y-1.5">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100/50 dark:hover:bg-slate-700 rounded-md transition-all duration-300 hover:scale-105"
                      aria-label="Cerrar sesión"
                    >
                      <svg
                        className="h-4 w-4 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <path d="M16 17l5-5-5-5" />
                        <path d="M21 12H9" />
                      </svg>
                      Cerrar sesión
                    </button>
                  </div>
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/5 to-purple-500/5 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Animaciones globales */}
      <style jsx>{`
        /* Mejora 1: Transiciones suaves para cambios de tema */
        html {
          transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .dark {
          transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-pulse-slow,
          .animate-bounce,
          .animate-ping,
          .animate-tilt,
          .animate-gradient-x {
            animation: none !important;
          }
          .transition-all,
          .transition-transform,
          .transition-opacity,
          .transition {
            transition: none !important;
          }
        }
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 4s ease infinite;
        }
        @keyframes tilt {
          0%, 100% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(0.8deg);
          }
          75% {
            transform: rotate(-0.8deg);
          }
        }
        .animate-tilt {
          animation: tilt 8s infinite linear;
        }
        @keyframes borderPulse {
          0%, 100% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
        }
        @keyframes pulse-slow {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.08);
            opacity: 0.75;
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2.6s ease-in-out infinite;
        }
        .glow {
          filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.5));
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
}